import { useEffect, memo } from 'react'
import { useMap } from 'react-leaflet'
import { createLogger } from '../utils/logger'

const logger = createLogger('DraggablePopupHandler')

/**
 * Component that enables dragging on popups to pan the map
 * Adds drag functionality to any popup with the 'draggable-popup' class
 * Supports both mouse and touch events for desktop and mobile
 * Also minimizes the main controls panel on click in immersive mode
 */
const DraggablePopupHandler = memo(function DraggablePopupHandler({ isImmersiveMode, isSearchPanelMinimized, setIsSearchPanelMinimized, onTabChange, activeTab, panelState }) {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    let isDragging = false
    let startPoint = null
    let currentPopup = null

    const handlePopupOpen = (e) => {
      const popup = e.popup
      if (!popup) return

      const popupElement = popup.getElement()
      if (!popupElement) return

      // Check if popup has draggable class
      if (!popupElement.classList.contains('draggable-popup')) {
        return
      }

      currentPopup = popup
      
      // State for this specific popup
      let popupDragging = false
      let popupStartPoint = null

      // Get coordinates from mouse or touch event
      const getEventCoords = (e) => {
        if (e.touches && e.touches.length > 0) {
          return { x: e.touches[0].clientX, y: e.touches[0].clientY }
        }
        return { x: e.clientX, y: e.clientY }
      }

      const onStart = (e) => {
        // Only handle left mouse button for mouse events
        if (e.type === 'mousedown' && e.button !== 0) return
        
        // Don't interfere with interactive elements
        if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || 
            e.target.closest('button') || e.target.closest('a') || 
            e.target.closest('input') || e.target.closest('textarea')) {
          return
        }
        
        const coords = getEventCoords(e)
        popupStartPoint = {
          x: coords.x,
          y: coords.y,
          center: map.getCenter(),
          moved: false
        }
        popupDragging = true
        e.preventDefault()
        e.stopPropagation()
      }

      const onMove = (e) => {
        if (!popupDragging || !popupStartPoint) return

        const coords = getEventCoords(e)
        const dx = coords.x - popupStartPoint.x
        const dy = coords.y - popupStartPoint.y

        // Mark as moved if distance > 5 pixels
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance > 5) {
          popupStartPoint.moved = true
        }

        if (!popupStartPoint.moved) return

        // Calculate new center based on drag distance
        const newPoint = map.project(popupStartPoint.center, map.getZoom())
          .subtract([dx, dy])
        
        const newCenter = map.unproject(newPoint, map.getZoom())
        map.panTo(newCenter, { animate: false, duration: 0 })

        e.preventDefault()
        e.stopPropagation()
      }

      const onEnd = (e) => {
        // If no movement (just a click), minimize panel in immersive mode
        if (popupDragging && popupStartPoint && !popupStartPoint.moved && isImmersiveMode) {
          if (onTabChange && activeTab && panelState !== 'minimized') {
            onTabChange(activeTab, 'minimized')
          } else if (setIsSearchPanelMinimized && !isSearchPanelMinimized) {
            setIsSearchPanelMinimized(true)
          }
        }
        
        popupDragging = false
        popupStartPoint = null
      }

      // Add event listeners to popup content
      const contentWrapper = popupElement.querySelector('.leaflet-popup-content-wrapper')
      
      if (contentWrapper) {
        // Mouse events
        contentWrapper.addEventListener('mousedown', onStart)
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onEnd)
        
        // Touch events for mobile
        contentWrapper.addEventListener('touchstart', onStart, { passive: false })
        document.addEventListener('touchmove', onMove, { passive: false })
        document.addEventListener('touchend', onEnd, { passive: false })
        document.addEventListener('touchcancel', onEnd, { passive: false })

        // Cleanup function stored on popup for later removal
        popup._draggableCleanup = () => {
          contentWrapper.removeEventListener('mousedown', onStart)
          document.removeEventListener('mousemove', onMove)
          document.removeEventListener('mouseup', onEnd)
          
          contentWrapper.removeEventListener('touchstart', onStart)
          document.removeEventListener('touchmove', onMove)
          document.removeEventListener('touchend', onEnd)
          document.removeEventListener('touchcancel', onEnd)
        }
      } else {
        logger.error('Content wrapper not found')
      }
    }

    const handlePopupClose = (e) => {
      const popup = e.popup
      if (popup && popup._draggableCleanup) {
        popup._draggableCleanup()
        delete popup._draggableCleanup
      }
      if (popup === currentPopup) {
        currentPopup = null
      }
    }

    map.on('popupopen', handlePopupOpen)
    map.on('popupclose', handlePopupClose)

    return () => {
      map.off('popupopen', handlePopupOpen)
      map.off('popupclose', handlePopupClose)
      
      // Cleanup any active popup
      if (currentPopup && currentPopup._draggableCleanup) {
        currentPopup._draggableCleanup()
      }
    }
  }, [map]) // Only depend on map - props are captured in closure

  return null
})

export default DraggablePopupHandler
